import { SOURCING_FILTER_KEYS, SUPPORTED_COMPETENCY_LEVELS } from '../constants';
import type { SourcingCompetencyLevel, SourcingFilterKey, SourcingFilters } from '../../../types';

export const isCompetencyLevel = (value: string): value is SourcingCompetencyLevel =>
  (SUPPORTED_COMPETENCY_LEVELS as readonly string[]).includes(value);

export const normalizeSpecializationId = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string' || !value.trim()) return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const normalizeTechnologyId = (value: unknown): string | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const id = String(value).trim();
  return id || undefined;
};

export const normalizeStricts = (value: unknown): SourcingFilterKey[] => {
  if (!Array.isArray(value)) return [];
  const validKeys = new Set<string>(SOURCING_FILTER_KEYS);
  return Array.from(
    new Set(value.filter((key): key is SourcingFilterKey => typeof key === 'string' && validKeys.has(key))),
  );
};

export const normalizeStringArray = <Value extends string>(value: Value[] | undefined): Value[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const normalized = Array.from(new Set(value.filter(item => typeof item === 'string' && Boolean(item))));
  return normalized.length > 0 ? normalized : undefined;
};

export const normalizeLastInteraction = (filters: Partial<SourcingFilters>): SourcingFilters['lastInteraction'] => {
  const candidate =
    filters.lastInteraction &&
    typeof filters.lastInteraction === 'object' &&
    (typeof filters.lastInteraction.from === 'string' || filters.lastInteraction.from === null) &&
    (typeof filters.lastInteraction.to === 'string' || filters.lastInteraction.to === null)
      ? {
          from: filters.lastInteraction.from,
          to: filters.lastInteraction.to,
        }
      : undefined;

  return candidate?.from || candidate?.to ? candidate : undefined;
};

export const getComparableValues = <Value extends string>(values: Value[] | null | undefined) =>
  values ? Array.from(new Set(values)).sort() : values;

export const hasFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (!value || typeof value !== 'object') return value !== null && value !== undefined && value !== '';

  return Boolean(('from' in value && value.from) || ('to' in value && value.to));
};
